import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SpamAlert {
  id: string;
  establishmentId: string;
  alertType: 'high_volume' | 'same_ip' | 'same_email' | 'suspicious_pattern';
  details: {
    count?: number;
    periodMinutes?: number;
    threshold?: number;
    emails?: string[];
    ip?: string;
    message?: string;
  };
  createdAt: Date;
  acknowledgedAt: Date | null;
  acknowledgedBy: string | null;
}

interface UseSpamAlertsOptions {
  establishmentId?: string;
}

export const useSpamAlerts = (options: UseSpamAlertsOptions = {}) => {
  const { establishmentId } = options;
  const [alerts, setAlerts] = useState<SpamAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);

  const fetchAlerts = async () => {
    if (!establishmentId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('spam_alerts')
        .select('*')
        .eq('establishment_id', establishmentId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const mappedAlerts: SpamAlert[] = (data || []).map((alert: any) => ({
        id: alert.id,
        establishmentId: alert.establishment_id,
        alertType: alert.alert_type,
        details: alert.details || {},
        createdAt: new Date(alert.created_at),
        acknowledgedAt: alert.acknowledged_at ? new Date(alert.acknowledged_at) : null,
        acknowledgedBy: alert.acknowledged_by,
      }));

      setAlerts(mappedAlerts);
      setUnacknowledgedCount(mappedAlerts.filter(a => !a.acknowledgedAt).length);
    } catch (error) {
      console.error('Error fetching spam alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeAlert = async (alertId: string, userId: string) => {
    try {
      const { error } = await supabase
        .from('spam_alerts')
        .update({
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: userId,
        })
        .eq('id', alertId);

      if (error) throw error;

      // Update local state
      setAlerts(prev =>
        prev.map(a =>
          a.id === alertId
            ? { ...a, acknowledgedAt: new Date(), acknowledgedBy: userId }
            : a
        )
      );
      setUnacknowledgedCount(prev => Math.max(0, prev - 1));

      return true;
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      return false;
    }
  };

  const acknowledgeAllAlerts = async (userId: string) => {
    if (!establishmentId) return false;

    try {
      const { error } = await supabase
        .from('spam_alerts')
        .update({
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: userId,
        })
        .eq('establishment_id', establishmentId)
        .is('acknowledged_at', null);

      if (error) throw error;

      // Update local state
      setAlerts(prev =>
        prev.map(a => ({
          ...a,
          acknowledgedAt: a.acknowledgedAt || new Date(),
          acknowledgedBy: a.acknowledgedBy || userId,
        }))
      );
      setUnacknowledgedCount(0);

      return true;
    } catch (error) {
      console.error('Error acknowledging all alerts:', error);
      return false;
    }
  };

  useEffect(() => {
    fetchAlerts();

    if (!establishmentId) return;

    // Real-time subscription for new alerts
    const subscription = supabase
      .channel(`spam_alerts_${establishmentId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'spam_alerts',
          filter: `establishment_id=eq.${establishmentId}`,
        },
        (payload) => {
          const newAlert: SpamAlert = {
            id: payload.new.id,
            establishmentId: payload.new.establishment_id,
            alertType: payload.new.alert_type,
            details: payload.new.details || {},
            createdAt: new Date(payload.new.created_at),
            acknowledgedAt: null,
            acknowledgedBy: null,
          };
          setAlerts(prev => [newAlert, ...prev]);
          setUnacknowledgedCount(prev => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'spam_alerts',
          filter: `establishment_id=eq.${establishmentId}`,
        },
        (payload) => {
          setAlerts(prev =>
            prev.map(a =>
              a.id === payload.new.id
                ? {
                    ...a,
                    acknowledgedAt: payload.new.acknowledged_at ? new Date(payload.new.acknowledged_at) : null,
                    acknowledgedBy: payload.new.acknowledged_by,
                  }
                : a
            )
          );
          // Recalculate unacknowledged count
          setAlerts(prev => {
            setUnacknowledgedCount(prev.filter(a => !a.acknowledgedAt).length);
            return prev;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [establishmentId]);

  return {
    alerts,
    unacknowledgedAlerts: alerts.filter(a => !a.acknowledgedAt),
    unacknowledgedCount,
    loading,
    acknowledgeAlert,
    acknowledgeAllAlerts,
    refetch: fetchAlerts,
  };
};
